import type { FC, ReactNode } from "react";
import { createContext, useDeferredValue, useEffect, use, Fragment, useState } from "react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { FormatDateOptions } from "../shared/formatDate";
import { isSupportedLanguage, type SupportedLanguage } from "../api/userInfo";
import { logError } from "../api/logError";
import { appConfig } from "../appConfig";
import { formatDate } from "../shared/formatDate";
import "dayjs/locale/en";
import "dayjs/locale/sv";
import "dayjs/locale/nb";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type sv from "./sv.json";
import { useLocalStorage } from "../shared/useLocalStorage";

type TranslationProviderProps = { children: ReactNode };

const LAST_USED_LANGUAGE_KEY = "privacy:last-used-language";

type Translations = typeof sv;

export type TranslationKey = keyof Translations;

const loadTranslations: Record<SupportedLanguage, () => Promise<Translations>> = {
  sv: () => import("./sv.json").then(module => module.default),
  no: () => import("./no.json").then(module => module.default),
  en: () => import("./en.json").then(module => module.default),
};

const translationKeys = { all: () => ["translations"] };

export const translationsQueryOptions = (language: SupportedLanguage) =>
  queryOptions({
    queryKey: [...translationKeys.all(), language],
    queryFn: loadTranslations[language],
    staleTime: Infinity,
    gcTime: Infinity,
  });

export type Translate = (key: TranslationKey, values?: Record<string, string | number>) => string;

type TranslationContextValue = {
  language: SupportedLanguage;
  t: Translate;
  formatDate: (date: string, options?: FormatDateOptions) => string;
  formatList: (items: readonly string[]) => string;
};

type TranslationConfigContextType = {
  setLanguage: (language: SupportedLanguage) => void;
  setIsE2EUser: (isE2EUser: boolean) => void;
};

const TranslationContext = createContext<TranslationContextValue | undefined>(undefined);
const TranslationConfigContext = createContext<TranslationConfigContextType | undefined>(undefined);

export const TranslationProvider = ({ children }: TranslationProviderProps) => {
  const [chosenLanguage, setLanguage] = useLocalStorage<SupportedLanguage>(
    LAST_USED_LANGUAGE_KEY,
    isSupportedLanguage,
    getBrowserLanguage()
  );
  const language = useDeferredValue(chosenLanguage);

  const [isE2EUser, setIsE2EUser] = useState(false);

  const { data: translations } = useSuspenseQuery(translationsQueryOptions(language));

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const contextValue: TranslationContextValue = {
    language,
    t: (key, values) => {
      if (appConfig.env.APP_ENV === "e2e" || isE2EUser) return key;

      const template = translations[key];

      if (!values) return template;

      const placeholderKeys = new Set<string>();
      const result = template.replace(/\{(\w+)\}/g, (_: unknown, placeholderKey: string) => {
        placeholderKeys.add(placeholderKey);
        return placeholderKey in values ? String(values[placeholderKey]) : `${placeholderKey}`;
      });

      const missingKeys = Array.from(placeholderKeys).filter(key => !(key in values));
      if (missingKeys.length > 0) {
        logError(
          `[Translation] Placeholders "${missingKeys.join(", ")}" are missing from values for translation key "${key}": "${template}"`
        );
      }

      return result;
    },
    formatDate: (date, options) => formatDate(date, language, options),
    formatList: items => new Intl.ListFormat(language, { type: "conjunction" }).format(items),
  };

  const configValue: TranslationConfigContextType = { setLanguage, setIsE2EUser };

  return (
    <TranslationConfigContext value={configValue}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language === "no" ? "nb" : language}>
        <TranslationContext value={contextValue}>{children}</TranslationContext>
      </LocalizationProvider>
    </TranslationConfigContext>
  );
};

type TranslateProps = { translationKey: TranslationKey; values?: Record<string, ReactNode> };

export const Translate: FC<TranslateProps> = ({ translationKey, values }) => {
  const { t } = useTranslation();
  const translation = t(translationKey);

  if (!values) return translation;

  const placeholderRegex = /\{(\w+)\}/g;

  const segments = translation.split(placeholderRegex);

  return (
    <>
      {segments.map((segment, i) => {
        if (i % 2 === 1) {
          const key = segment;
          const element = values[key];

          if (element === undefined) {
            logError(
              new Error(`[Translation] Placeholder "{${key}}" is missing from values for translation: "${translation}"`)
            );
            return null;
          }

          return <Fragment key={i}>{element}</Fragment>;
        }

        return segment;
      })}
    </>
  );
};

const useTranslationConfig = () => {
  const context = use(TranslationConfigContext);
  if (!context) throw new Error("useTranslationConfig must be used within a TranslationProvider");
  return context;
};

export const useSyncTranslationConfig = ({
  isE2eUser,
  language,
}: {
  isE2eUser?: boolean;
  language: SupportedLanguage;
}) => {
  const translationConfig = useTranslationConfig();
  useEffect(() => {
    translationConfig.setLanguage(language);
    if (isE2eUser) translationConfig.setIsE2EUser(isE2eUser);
  }, [language, isE2eUser, translationConfig]);
};

export const useTranslation = () => {
  const context = use(TranslationContext);
  if (!context) throw new Error("useTranslation must be used within a TranslationProvider");
  return context;
};

const getBrowserLanguage = (): SupportedLanguage => {
  /** If primary language is not supported, check the list of user's preferred languages */
  for (const lang of navigator.languages) {
    const baseLang = lang.split("-")[0];
    if (isSupportedLanguage(baseLang)) return baseLang;
  }
  /** Return fallback languages if none of the user's preferred languages are supported */
  return "sv";
};
